import Tombstone from "@components/Tombstone";
import AccentButton from "@components/ui/AccentButton";
import { useAppContext } from "@contexts/AppContext";
import {
	Box,
	Button,
	Chip,
	Dialog,
	DialogContent,
	Divider,
	Fab,
	IconButton,
	Paper,
	Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import HelpIcon from "@material-ui/icons/Help";
import InfoIcon from "@material-ui/icons/Info";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import useCartStore, { SERVICE_TYPES } from "@stores/cartStore";
import useLanguageStore from "@stores/languageStore";
import React, { Suspense, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import SEOHead from "../../components/SEOHead";
import LeadGate from "../../components/LeadGate";

const TombstonePage = () => {
	const { t } = useTranslation();
	const { language } = useLanguageStore();
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const editItemId = searchParams.get("edit");
	const isRtl = language === "he";
	const addItem = useCartStore((state) => state.addItem);
	const updateItem = useCartStore((state) => state.updateItem);
	const getItemById = useCartStore((state) => state.getItemById);
	const { showSuccess } = useAppContext();
	const previewTitleId = useId();

	const [pillowColor, setPillowColor] = useState("gray");
	const [mainColor, setMainColor] = useState("#323131");
	const [baseColor, setBaseColor] = useState("gray");
	const [showPreview, setShowPreview] = useState(false);

	// Load cart item data if in edit mode
	useEffect(() => {
		if (editItemId) {
			const item = getItemById(editItemId);
			if (item && item.serviceType === SERVICE_TYPES.TOMBSTONE) {
				if (item.data.pillowColor) setPillowColor(item.data.pillowColor);
				if (item.data.mainStoneColor) setMainColor(item.data.mainStoneColor);
				if (item.data.baseColor) setBaseColor(item.data.baseColor);
			}
		}
	}, [editItemId, getItemById]);

	const colorOptions = [
		{ value: "gray", label: t("tombstone.gray") },
		{ value: "#323131", label: t("tombstone.black") },
		{ value: "natural", label: t("tombstone.natural") },
	];

	return (
		<div className="h-[calc(100vh-64px)] flex flex-col md:flex-row overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 max-w-[100vw]">
			<LeadGate />
			<SEOHead
				title={t("tombstone.title")}
				description={t("tombstone.subtitle")}
			/>
			{/* 3D Display Area - Desktop: Always visible */}
			<div
				className={`hidden md:flex flex-1 items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden ${
					isRtl ? "order-2" : "order-1"
				}`}
				style={{ height: "100%" }}
			>
				<Suspense
					fallback={
						<div className="flex flex-col justify-center items-center text-xl text-gray-600">
							<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
							{t("common.loading")}
						</div>
					}
				>
					<Tombstone
						pillowColor={pillowColor}
						mainColor={mainColor}
						baseColor={baseColor}
					/>
				</Suspense>
			</div>

			{/* Mobile Preview - Modal/Portal */}
			<Dialog
				open={showPreview}
				onClose={() => setShowPreview(false)}
				fullScreen
				className="md:hidden"
				aria-labelledby={previewTitleId}
				PaperProps={{
					style: {
						margin: 0,
						maxHeight: "100vh",
						borderRadius: 0,
						backgroundColor: "transparent",
					},
				}}
			>
				<DialogContent
					style={{ padding: 0, overflow: "hidden", height: "100vh" }}
				>
					<div
						style={{
							position: "relative",
							width: "100%",
							height: "100%",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							backgroundColor: "rgba(0, 0, 0, 0.5)",
						}}
					>
						<h2 id={previewTitleId} className="sr-only">
							{t("tombstone.preview")}
						</h2>
						<IconButton
							onClick={() => setShowPreview(false)}
							style={{
								position: "absolute",
								top: 16,
								right: isRtl ? "auto" : 16,
								left: isRtl ? 16 : "auto",
								zIndex: 1000,
								backgroundColor: "rgba(255, 255, 255, 0.9)",
							}}
							aria-label={t("common.close")}
						>
							<CloseIcon />
						</IconButton>
						<div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
							<Suspense
								fallback={
									<div className="flex flex-col justify-center items-center text-xl text-gray-600">
										<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
										{t("common.loading")}
									</div>
								}
							>
								<Tombstone
									pillowColor={pillowColor}
									mainColor={mainColor}
									baseColor={baseColor}
								/>
							</Suspense>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* Floating Preview Toggle Button - Mobile Only */}
			<Fab
				color="primary"
				aria-label="toggle preview"
				onClick={() => setShowPreview(!showPreview)}
				className="md:hidden"
				style={{
					position: "fixed",
					bottom: "24px",
					right: isRtl ? "auto" : "24px",
					left: isRtl ? "24px" : "auto",
					zIndex: 1000,
					backgroundColor: "#424242",
					color: "white",
				}}
			>
				{showPreview ? <VisibilityOffIcon /> : <VisibilityIcon />}
			</Fab>

			{/* Control Panel */}
			<Paper
				className={`w-full md:w-[420px] mt-[3em] md:mt-[4em] flex flex-col bg-white shadow-2xl overflow-hidden border-l border-stone-200 relative ${
					isRtl ? "order-1 border-l-0 border-r md:order-1" : "order-2"
				}`}
				elevation={8}
				style={{ height: "100%", maxHeight: "100%" }}
			>
				{/* Header */}
				<div className="bg-white text-black p-4 md:p-6 shadow-md">
					<Typography
						variant="h4"
						component="h1"
						className="!font-extrabold text-center mb-2"
					>
						{t("tombstone.title")}
					</Typography>
					<Typography
						variant="body2"
						className="text-center opacity-90 !text-sm"
					>
						{t("tombstone.subtitle")}
					</Typography>
				</div>

				{/* Scrollable Content */}
				<div className="flex-1 overflow-y-auto p-6 pb-28">
					{/* Instructions Section */}
					<Paper
						className="mb-6 p-4 bg-stone-50 border-l-4 border-stone-600"
						elevation={0}
					>
						<div className="flex flex-col gap-3">
							<span className="flex items-center gap-2">
								<InfoIcon className="text-[#063447]" />
								<Typography
									variant="subtitle2"
									component="h2"
									className="mb-2 font-bold text-black"
								>
									{t("tombstone.instructions.title")}
								</Typography>
							</span>
							<Box>
								<ul
									className={`list-disc list-inside text-sm text-black space-y-1 p-2 m-0 ${
										isRtl ? "text-right" : "text-left"
									}`}
								>
									<li>{t("tombstone.instructions.step1")}</li>
									<li>{t("tombstone.instructions.step2")}</li>
									<li>{t("tombstone.instructions.step3")}</li>
									<li>{t("tombstone.instructions.step4")}</li>
								</ul>
							</Box>
						</div>
					</Paper>

					{/* Color Selection */}
					<Typography
						variant="h6"
						component="h2"
						className={`mb-6 !font-bold text-black ${isRtl ? "text-right" : "text-left"}`}
					>
						{t("tombstone.colorSelection")}
					</Typography>

					{/* Pillow Selection */}
					<Box className="mb-8">
						<Typography
							variant="subtitle1"
							component="h3"
							className={`mb-4 font-semibold text-gray-800 ${isRtl ? "text-right" : "text-left"}`}
						>
							{t("tombstone.pillow")}
						</Typography>
						<Box className="flex flex-col gap-3">
							{colorOptions.map((option) => (
								<Button
									key={option.value}
									variant={
										pillowColor === option.value ? "contained" : "outlined"
									}
									style={
										pillowColor === option.value
											? { backgroundColor: "#063447", color: "white" }
											: {}
									}
									onClick={() => setPillowColor(option.value)}
									className={`py-3 px-4 normal-case text-base font-medium transition-all ${
										pillowColor === option.value
											? "transform scale-[1.02]"
											: "hover:bg-[#063447]/10"
									}`}
									fullWidth
								>
									{option.label}
								</Button>
							))}
						</Box>
					</Box>

					<Divider className="my-6" />

					{/* Main Stone Selection */}
					<Box className="mb-8">
						<Typography
							variant="subtitle1"
							component="h3"
							className={`mb-4 font-semibold text-gray-800 ${isRtl ? "text-right" : "text-left"}`}
						>
							{t("tombstone.mainStone")}
						</Typography>
						<Box className="flex flex-col gap-3">
							{colorOptions.map((option) => (
								<Button
									key={option.value}
									variant={
										mainColor === option.value ? "contained" : "outlined"
									}
									style={
										mainColor === option.value
											? { backgroundColor: "#063447", color: "white" }
											: {}
									}
									onClick={() => setMainColor(option.value)}
									className={`py-3 px-4 normal-case text-base font-medium transition-all ${
										mainColor === option.value
											? "transform scale-[1.02]"
											: "hover:bg-gray-50"
									}`}
									fullWidth
								>
									{option.label}
								</Button>
							))}
						</Box>
					</Box>

					<Divider className="my-6" />

					{/* Base Selection */}
					<Box className="mb-8">
						<Typography
							variant="subtitle1"
							component="h3"
							className={`mb-4 font-semibold text-gray-800 ${isRtl ? "text-right" : "text-left"}`}
						>
							{t("tombstone.base")}
						</Typography>
						<Box className="flex flex-col gap-3">
							{colorOptions.map((option) => (
								<Button
									key={option.value}
									variant={
										baseColor === option.value ? "contained" : "outlined"
									}
									style={
										baseColor === option.value
											? { backgroundColor: "#063447", color: "white" }
											: {}
									}
									onClick={() => setBaseColor(option.value)}
									className={`py-3 px-4 normal-case text-base font-medium transition-all ${
										baseColor === option.value
											? "shadow-md transform scale-[1.02]"
											: "hover:bg-[#424247]/10"
									}`}
									fullWidth
								>
									{option.label}
								</Button>
							))}
						</Box>
					</Box>

					{/* Design Tips */}
					<Paper
						className="mt-8 p-4 bg-amber-50 border-l-4 border-amber-400"
						elevation={0}
					>
						<div className="flex flex-col gap-3">
							<span className="flex items-center gap-2">
								<HelpIcon className="text-[#063447]" />
								<Typography
									variant="subtitle2"
									component="h2"
									className="mb-3 !font-bold text-[#063447]"
								>
									{t("tombstone.tips.title")}
								</Typography>
							</span>
							<Box>
								<ul className="list-disc list-inside text-sm text-black space-y-1 p-2 m-0">
									<li>{t("tombstone.tips.tip1")}</li>
									<li>{t("tombstone.tips.tip2")}</li>
									<li>{t("tombstone.tips.tip3")}</li>
									<li>{t("tombstone.tips.tip4")}</li>
								</ul>
							</Box>
						</div>

						<div className="md:hidden fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t border-gray-200 shadow-lg">
							<AccentButton
								size="large"
								fullWidth
								onClick={() => {
									const itemData = {
										pillowColor,
										mainStoneColor: mainColor,
										baseColor,
									};

									if (editItemId) {
										updateItem(editItemId, itemData);
										showSuccess(t("tombstone.updatedInCart"));
										setTimeout(() => {
											navigate("/cart");
										}, 1000);
									} else {
										addItem({
											serviceType: SERVICE_TYPES.TOMBSTONE,
											data: itemData,
										});
										showSuccess(t("tombstone.addedToCart"));
									}
								}}
								className="py-3 normal-case font-semibold"
							>
								{editItemId
									? t("tombstone.updateCart")
									: t("tombstone.addToCart")}
							</AccentButton>
						</div>
						<AccentButton
							size="large"
							fullWidth
							onClick={() => {
								const itemData = {
									pillowColor,
									mainStoneColor: mainColor,
									baseColor,
								};

								if (editItemId) {
									updateItem(editItemId, itemData);
									showSuccess(t("tombstone.updatedInCart"));
									setTimeout(() => {
										navigate("/cart");
									}, 1000);
								} else {
									addItem({
										serviceType: SERVICE_TYPES.TOMBSTONE,
										data: itemData,
									});
									showSuccess(t("tombstone.addedToCart"));
								}
							}}
							className="py-3 normal-case font-semibold !mt-8 hidden md:block"
						>
							{editItemId
								? t("tombstone.updateCart")
								: t("tombstone.addToCart")}
						</AccentButton>
					</Paper>
					<div className="md:hidden h-20" />
				</div>
			</Paper>
		</div>
	);
};

export default TombstonePage;
